/**
 * ATTRIBUTION MARKETING
 * Capture des paramètres d'attribution (utm_*, click ids) présents dans
 * l'URL de la page publique de formulaire. Le site les propage dans le src
 * de l'iframe ; on fige le payload dans form_submissions.attribution au
 * moment de la soumission.
 *
 * Allowlist alignée sur 4l-trophy/src/lib/tracking-url.ts.
 */

const ATTRIBUTION_PARAM_ALLOWLIST = [
  // Campaign tagging standard (GA4 channel grouping)
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
  // Click identifiers réseaux pub
  'fbclid', // Meta
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS app→web)
  'wbraid', // Google Ads (web→app)
  'dclid', // Display & Video 360
  'gad_source', // Google Ads
  'msclkid', // Microsoft Ads
  'ttclid', // TikTok
  'twclid', // X/Twitter
  'li_fat_id', // LinkedIn
  'epik', // Pinterest
] as const;

/** Longueur max par valeur (une valeur légitime fait quelques dizaines de caractères) */
const MAX_VALUE_LENGTH = 256;

/**
 * Budget total du payload sérialisé, en OCTETS UTF-8 — la contrainte SQL
 * (form_submissions_attribution_max_size) mesure octet_length(...::text),
 * pas des caractères : un payload accentué pèse plus lourd que sa longueur
 * JS, et la sérialisation canonique jsonb ajoute un espace après chaque ':'
 * et ',' (~2 octets par clé). 1900 garde cette marge sous les 2048 de la
 * contrainte. Au-delà, on abandonne l'attribution plutôt que de risquer de
 * bloquer la soumission.
 */
const MAX_PAYLOAD_BYTES = 1900;

// Coupe à MAX_VALUE_LENGTH sans laisser de high surrogate orphelin en fin de
// chaîne (un emoji coupé en deux donnerait un JSON que le jsonb de Postgres
// rejette — et donc une soumission bloquée).
function truncateValue(value: string): string {
  const truncated = value.slice(0, MAX_VALUE_LENGTH);
  const lastCode = truncated.charCodeAt(truncated.length - 1);
  return lastCode >= 0xd800 && lastCode <= 0xdbff ? truncated.slice(0, -1) : truncated;
}

/**
 * Extrait les paramètres d'attribution d'une query string.
 * Retourne null si aucun paramètre allowlisté n'est présent.
 */
export function extractAttributionFromSearch(search: string): Record<string, string> | null {
  const params = new URLSearchParams(search);
  const attribution: Record<string, string> = {};

  for (const key of ATTRIBUTION_PARAM_ALLOWLIST) {
    // Strip des octets NUL (%00 décodé) : le jsonb de Postgres rejette \u0000,
    // ce qui ferait échouer l'INSERT entier de la soumission.
    const value = params.get(key)?.replaceAll('\u0000', '').trim();
    if (value) {
      attribution[key] = truncateValue(value);
    }
  }

  if (Object.keys(attribution).length === 0) {
    return null;
  }
  if (new TextEncoder().encode(JSON.stringify(attribution)).length > MAX_PAYLOAD_BYTES) {
    return null;
  }
  return attribution;
}
